import {
  Box,
  Button,
  CircularProgress,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import RefreshIcon from "@mui/icons-material/Refresh";

export const treasuryFieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "12px",
    bgcolor: "rgba(255,255,255,0.85)",
    fontSize: "14px",
    fontFamily: '"Open Sans", Helvetica, Arial, sans-serif',
    "& fieldset": {
      borderColor: "rgba(40, 80, 160, 0.3)",
    },
    "&:hover fieldset": {
      borderColor: "rgba(40, 80, 160, 0.6)",
    },
    "&.Mui-focused fieldset": {
      borderColor: "rgba(14, 50, 149, 1)",
    },
  },
  "& .MuiInputLabel-root": {
    fontFamily: '"Open Sans", Helvetica, Arial, sans-serif',
    fontSize: "13px",
    fontWeight: 600,
    color: "rgba(2, 8, 23, 0.7)",
  },
  "& .MuiFormHelperText-root": {
    ml: 0.5,
    mt: 0.6,
    fontSize: "12px",
    fontWeight: 600,
  },
};

export function StyledTreasuryField(props) {
  return (
    <TextField
      {...props}
      fullWidth
      size="small"
      variant="outlined"
      sx={treasuryFieldSx}
    />
  );
}

export function TreasuryPanelFrame({
  title,
  description,
  onRefresh,
  refreshing,
  saving,
  children,
}) {
  return (
    <Paper
      sx={{
        p: { xs: 2, md: 2.5 },
        borderRadius: "24px",
        bgcolor: "neutral.200",
        boxShadow: "inset 0px 0px 6px rgba(64,64,94,0.25)",
      }}
    >
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={1.5}
        alignItems={{ md: "center" }}
        justifyContent="space-between"
      >
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: 20, fontWeight: 900, color: "primary.900" }}>
            {title}
          </Typography>
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: "neutral.700", mt: 0.5 }}>
            {description}
          </Typography>
        </Box>

        {onRefresh ? (
          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={onRefresh}
            disabled={refreshing || saving}
            sx={{
              alignSelf: { xs: "flex-start", md: "center" },
              borderRadius: "33px",
              px: 2.5,
              py: 1,
              textTransform: "none",
              fontWeight: 700,
            }}
          >
            Actualizar lista
          </Button>
        ) : null}
      </Stack>

      <Stack spacing={3} sx={{ mt: 2 }}>
        {children}
      </Stack>
    </Paper>
  );
}

export function TreasuryTable({
  columns,
  rows,
  loading,
  emptyMessage,
  selectedId,
  onEdit,
  onDelete,
  saving,
}) {
  const colSpan = columns.length + 1;

  return (
    <TableContainer
      component={Box}
      sx={{
        borderRadius: "24px",
        bgcolor: "neutral.50",
        boxShadow: "0 14px 36px rgba(15, 23, 42, 0.08)",
        maxHeight: "40vh",
        overflow: "auto",
      }}
    >
      <Table stickyHeader size="small">
        <TableHead>
          <TableRow>
            {columns.map((column) => (
              <TableCell key={column.key} sx={{ fontWeight: 900, bgcolor: "neutral.300" }}>
                {column.label}
              </TableCell>
            ))}
            <TableCell align="right" sx={{ fontWeight: 900, bgcolor: "neutral.300" }}>
              Acciones
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={colSpan} align="center" sx={{ py: 5 }}>
                <CircularProgress size={28} />
              </TableCell>
            </TableRow>
          ) : null}

          {!loading && rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={colSpan} align="center" sx={{ py: 5, color: "neutral.700", fontWeight: 700 }}>
                {emptyMessage}
              </TableCell>
            </TableRow>
          ) : null}

          {!loading
            ? rows.map((row) => {
                const active = String(row.id) === String(selectedId || "");
                return (
                  <TableRow
                    key={row.id}
                    hover
                    selected={active}
                    sx={{
                      "&.Mui-selected": {
                        bgcolor: "rgba(14, 50, 149, 0.08)",
                      },
                    }}
                  >
                    {columns.map((column) => (
                      <TableCell key={column.key} sx={{ fontWeight: column.strong ? 800 : 600, color: column.strong ? "primary.900" : "neutral.800" }}>
                        {column.render ? column.render(row) : row[column.key]}
                      </TableCell>
                    ))}
                    <TableCell align="right">
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Button
                          size="small"
                          variant="outlined"
                          color="secondary"
                          startIcon={<EditIcon />}
                          onClick={() => onEdit(row)}
                          disabled={saving}
                          sx={{ borderRadius: "999px", textTransform: "none", fontWeight: 800 }}
                        >
                          Editar
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          startIcon={<DeleteOutlineIcon />}
                          onClick={() => onDelete(row)}
                          disabled={saving}
                          sx={{ borderRadius: "999px", textTransform: "none", fontWeight: 800 }}
                        >
                          Eliminar
                        </Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })
            : null}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export function renderSelectOptions(options) {
  return options.map((option) => (
    <MenuItem key={option.value} value={option.value}>
      {option.label}
    </MenuItem>
  ));
}
